import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Check, Plus } from 'lucide-react';

import { api } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { SingleSelect, MultiSelect } from './DropdownField.jsx';
import './AddToPipelineModal.css';

/**
 * Create a new investor (fund) record.
 *   - admin / team can create with any fields
 *   - client can create — name + website are required, and they become the owner
 */
export function NewInvestorModal({ onClose, onCreated }) {
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const isClient = user?.role === 'client';

    // Load dropdown options
    const { data: settings } = useQuery({
        queryKey: ['settings'],
        queryFn: () => api.get('/settings'),
    });

    const sectorOptions    = Array.isArray(settings?.sectors)            ? settings.sectors            : [];
    const investorTypeOpts = Array.isArray(settings?.investor_types)     ? settings.investor_types     : [];
    const stageOptions     = Array.isArray(settings?.investment_stages)  ? settings.investment_stages  : [];
    const financingOptions = Array.isArray(settings?.financing_types)    ? settings.financing_types    : [];
    const geoOptions       = Array.isArray(settings?.geo_options)        ? settings.geo_options        : [];
    const fitLevelOptions  = Array.isArray(settings?.fit_levels)         ? settings.fit_levels         : [];
    const statusOptions    = Array.isArray(settings?.status_options)     ? settings.status_options     : [];

    const [form, setForm] = useState({
        name: '',
        website: '',
        // Dropdown-backed fields (will be SingleSelect or MultiSelect)
        investor_type: '',
        sector_fit: '',
        investment_stage: '',
        financing_type: '',
        geo_focus: '',
        fit_level: '',
        status: '',
        // Free text
        country: '',
        // Ticket sweetspot
        investment_range_min: '',
        investment_range_max: '',
        expected_ticket: '',
        // Other
        fit_rationale: '',
        relation: '',
        sdgs: '',
        particular_conditions: '',
        notes: '',
        primary_contact_name: '',
        primary_contact_email: '',
        primary_contact_linkedin: '',
    });

    const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));
    const setValue = (field) => (newValue) => setForm(prev => ({ ...prev, [field]: newValue || '' }));

    const createMutation = useMutation({
        mutationFn: async (payload) => {
            const investor = await api.post('/investors', payload);
            if (payload._contact && (payload._contact.name || payload._contact.email)) {
                await api.post(`/investors/${investor.id}/contacts`, payload._contact);
            }
            return investor;
        },
        onSuccess: (investor) => {
            queryClient.invalidateQueries({ queryKey: ['investors'] });
            onCreated?.(investor);
            onClose();
        },
    });

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!form.name.trim()) return;
        if (isClient && !form.website.trim()) return;

        const payload = { name: form.name.trim() };

        const textFields = [
            'website', 'investor_type', 'sector_fit', 'investment_stage',
            'financing_type', 'country', 'geo_focus', 'expected_ticket',
            'fit_level', 'fit_rationale', 'status', 'relation', 'sdgs',
            'particular_conditions', 'notes',
        ];
        for (const f of textFields) {
            if (form[f] && form[f].toString().trim()) payload[f] = form[f].toString().trim();
        }

        if (form.investment_range_min) {
            const n = Number(form.investment_range_min);
            if (!isNaN(n)) payload.investment_range_min = n;
        }
        if (form.investment_range_max) {
            const n = Number(form.investment_range_max);
            if (!isNaN(n)) payload.investment_range_max = n;
        }

        const contact = {};
        if (form.primary_contact_name.trim())     contact.name     = form.primary_contact_name.trim();
        if (form.primary_contact_email.trim())    contact.email    = form.primary_contact_email.trim();
        if (form.primary_contact_linkedin.trim()) contact.linkedin = form.primary_contact_linkedin.trim();
        if (Object.keys(contact).length > 0) payload._contact = contact;

        createMutation.mutate(payload);
    };

    const canSubmit = form.name.trim() && (!isClient || form.website.trim()) && !createMutation.isPending;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-dialog modal-dialog-wide" onClick={(e) => e.stopPropagation()}>
                <header className="modal-header">
                    <div>
                        <h2 className="modal-title">Add a new investor</h2>
                        <p className="modal-subtitle">
                            {isClient
                                ? 'Add an investor from your own network. You\'ll be marked as the introducer.'
                                : 'Fill in everything you know — leave the rest blank for later.'}
                        </p>
                    </div>
                    <button className="modal-close" onClick={onClose}><X size={16} strokeWidth={2}/></button>
                </header>

                <form className="modal-body modal-body-scroll" onSubmit={handleSubmit}>

                    <Section title="Basics">
                        <div className="contact-form-grid">
                            <Field label="Fund / firm name *" required>
                                <input required value={form.name} onChange={set('name')}
                                       className="detail-field-input" placeholder="e.g. Triodos"
                                       autoFocus/>
                            </Field>
                            <Field label={`Website ${isClient ? '*' : ''}`}>
                                <input type="url" value={form.website} onChange={set('website')}
                                       required={isClient}
                                       className="detail-field-input"
                                       placeholder="https://..."/>
                            </Field>
                            <Field label="Country">
                                <input value={form.country} onChange={set('country')}
                                       className="detail-field-input"
                                       placeholder="Netherlands"/>
                            </Field>
                            <Field label="Geographic focus" noLabel>
                                <MultiSelect value={form.geo_focus} options={geoOptions}
                                             onChange={setValue('geo_focus')}
                                             placeholder="Europe, DACH, Benelux..."/>
                            </Field>
                        </div>
                    </Section>

                    <Section title="Investment profile">
                        <div className="contact-form-grid">
                            <Field label="Investor type">
                                <SingleSelect value={form.investor_type} options={investorTypeOpts}
                                              onChange={setValue('investor_type')}
                                              placeholder="Angel, VC, Family Office..."/>
                            </Field>
                            <Field label="Investment stage" noLabel>
                                <MultiSelect value={form.investment_stage} options={stageOptions}
                                             onChange={setValue('investment_stage')}
                                             placeholder="Pre-seed, Seed, Series A..."/>
                            </Field>
                            <Field label="Sector focus" noLabel>
                                <MultiSelect value={form.sector_fit} options={sectorOptions}
                                             onChange={setValue('sector_fit')}
                                             placeholder="Climate tech, Foodtech..."/>
                            </Field>
                            <Field label="Financing type" noLabel>
                                <MultiSelect value={form.financing_type} options={financingOptions}
                                             onChange={setValue('financing_type')}
                                             placeholder="Equity, Debt, Convertible..."/>
                            </Field>
                        </div>
                    </Section>

                    <Section title="Ticket sweetspot">
                        <div className="contact-form-grid">
                            <Field label="Range min (€)">
                                <input type="number" value={form.investment_range_min}
                                       onChange={set('investment_range_min')}
                                       className="detail-field-input mono"
                                       placeholder="250000"/>
                            </Field>
                            <Field label="Range max (€)">
                                <input type="number" value={form.investment_range_max}
                                       onChange={set('investment_range_max')}
                                       className="detail-field-input mono"
                                       placeholder="2000000"/>
                            </Field>
                            <Field label="Ticket sweetspot (label)">
                                <input value={form.expected_ticket} onChange={set('expected_ticket')}
                                       className="detail-field-input"
                                       placeholder="€250k–€2M"/>
                            </Field>
                        </div>
                    </Section>

                    <Section title="Fit & status">
                        <div className="contact-form-grid">
                            <Field label="Fit level">
                                <SingleSelect value={form.fit_level} options={fitLevelOptions}
                                              onChange={setValue('fit_level')}
                                              placeholder="High, Medium, Low..."/>
                            </Field>
                            <Field label="Status">
                                <SingleSelect value={form.status} options={statusOptions}
                                              onChange={setValue('status')}
                                              placeholder="New Lead, Hot, Warm..."/>
                            </Field>
                            <Field label="Relation">
                                <input value={form.relation} onChange={set('relation')}
                                       className="detail-field-input"
                                       placeholder="How do we know them?"/>
                            </Field>
                            <Field label="SDGs">
                                <input value={form.sdgs} onChange={set('sdgs')}
                                       className="detail-field-input"
                                       placeholder="2, 7, 13"/>
                            </Field>
                        </div>
                        <Field label="Fit rationale">
                            <textarea rows={2} value={form.fit_rationale}
                                      onChange={set('fit_rationale')}
                                      className="detail-field-input"
                                      placeholder="Why is this a good fit (or not)?"/>
                        </Field>
                        <Field label="Particular conditions">
                            <textarea rows={2} value={form.particular_conditions}
                                      onChange={set('particular_conditions')}
                                      className="detail-field-input"
                                      placeholder="Specific requirements or restrictions"/>
                        </Field>
                    </Section>

                    <Section title="Primary contact (optional)">
                        <div className="contact-form-grid">
                            <Field label="Contact name">
                                <input value={form.primary_contact_name}
                                       onChange={set('primary_contact_name')}
                                       className="detail-field-input"
                                       placeholder="Name of person at fund"/>
                            </Field>
                            <Field label="Contact email">
                                <input type="email" value={form.primary_contact_email}
                                       onChange={set('primary_contact_email')}
                                       className="detail-field-input"
                                       placeholder="email@fund.com"/>
                            </Field>
                        </div>
                        <Field label="LinkedIn">
                            <input type="url" value={form.primary_contact_linkedin}
                                   onChange={set('primary_contact_linkedin')}
                                   className="detail-field-input"
                                   placeholder="https://linkedin.com/in/..."/>
                        </Field>
                    </Section>

                    <Section title="Notes">
                        <Field label="Internal notes">
                            <textarea rows={3} value={form.notes} onChange={set('notes')}
                                      className="detail-field-input"
                                      placeholder="Anything noteworthy about this fund or relationship"/>
                        </Field>
                    </Section>

                    {createMutation.isError && (
                        <div className="panel-error">{createMutation.error.message}</div>
                    )}

                    <div className="modal-actions modal-actions-sticky">
                        <button type="button" className="btn-ghost" onClick={onClose}
                                disabled={createMutation.isPending}>
                            <X size={14} strokeWidth={2}/> Cancel
                        </button>
                        <button type="submit" className="btn-save" disabled={!canSubmit}>
                            {createMutation.isPending
                                ? <span className="spinner"/>
                                : <><Plus size={14} strokeWidth={2}/> Create investor</>}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <fieldset className="form-section">
            <legend className="form-section-title">{title}</legend>
            {children}
        </fieldset>
    );
}

function Field({ label, required, children, noLabel }) {
    const El = noLabel ? 'div' : 'label';
    return (
        <El className="detail-field">
            <span className="detail-field-label">
                {label}{required && ' *'}
            </span>
            {children}
        </El>
    );
}
